import { useDraggable } from '@dnd-kit/core';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { User, Package, Building2, Clock, Pause, Calendar, AlertTriangle, Layers } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  quantity: number;
  priority: string;
  planning_status: string;
  is_paused: boolean;
  assigned_operator: string | null;
  mold_id: string | null;
  customer?: { company_name: string };
  operator?: { full_name: string };
  project?: { name: string };
  due_date?: string;
  scheduled_start?: string;
  plybook?: { name: string; version: string };
  process_records?: Array<{ status: string; manufacturing_processes?: { name: string } }>;
}

interface KanbanCardProps {
  order: WorkOrder;
  onClick?: () => void;
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'border-l-red-500 bg-red-500/5',
  high: 'border-l-orange-500 bg-orange-500/5',
  normal: 'border-l-blue-500 bg-blue-500/5',
  low: 'border-l-gray-400 bg-gray-400/5',
};

const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baja',
};

export function KanbanCard({ order, onClick }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.id,
  });

  const style = transform ? {
    transform: 'translate3d(' + transform.x + 'px, ' + transform.y + 'px, 0)',
  } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <WorkOrderCard order={order} isDragging={isDragging} onClick={onClick} />
    </div>
  );
}

interface WorkOrderCardProps {
  order: WorkOrder;
  isDragging?: boolean;
  onClick?: () => void;
}

function HoverContent({ order }: { order: WorkOrder }) {
  const pendingProcesses = order.process_records?.filter(r => r.status === 'pending') || [];
  const isOverdue = order.due_date && new Date(order.due_date) < new Date();

  return (
    <div className="space-y-3 text-sm">
      <div>
        <div className="font-semibold text-base">{order.order_number}</div>
        <div className="text-muted-foreground">{order.product_name}</div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-muted-foreground">Cliente:</span>
          <div className="font-medium">{order.customer?.company_name || 'Sin cliente'}</div>
        </div>
        {order.project && (
          <div>
            <span className="text-muted-foreground">Proyecto:</span>
            <div className="font-medium">{order.project.name}</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-muted-foreground">Cantidad:</span>
          <div className="font-medium">{order.quantity} unidades</div>
        </div>
        <div>
          <span className="text-muted-foreground">Prioridad:</span>
          <Badge variant={order.priority === 'urgent' ? 'destructive' : 'outline'} className="ml-1">
            {PRIORITY_LABELS[order.priority] || order.priority}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-muted-foreground">Operario:</span>
          <div className={order.operator ? 'font-medium' : 'text-amber-500'}>
            {order.operator?.full_name || 'Sin asignar'}
          </div>
        </div>
        {order.due_date && (
          <div>
            <span className="text-muted-foreground">Fecha límite:</span>
            <div className={isOverdue ? 'text-red-500 font-medium' : 'font-medium'}>
              {format(new Date(order.due_date), 'dd MMM yyyy', { locale: es })}
              {isOverdue && <AlertTriangle className="w-3 h-3 inline ml-1" />}
            </div>
          </div>
        )}
      </div>

      {order.plybook && (
        <div>
          <span className="text-muted-foreground">Plybook:</span>
          <div className="font-medium">{order.plybook.name} v{order.plybook.version}</div>
        </div>
      )}

      {pendingProcesses.length > 0 && (
        <div>
          <span className="text-muted-foreground">Procesos pendientes:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {pendingProcesses.slice(0, 4).map((p, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {p.manufacturing_processes?.name || 'Proceso'}
              </Badge>
            ))}
            {pendingProcesses.length > 4 && (
              <Badge variant="secondary" className="text-xs">+{pendingProcesses.length - 4}</Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function WorkOrderCard({ order, isDragging, onClick }: WorkOrderCardProps) {
  const cardContent = (
    <Card
      className={'p-3 border-l-4 cursor-grab active:cursor-grabbing transition-all ' +
        (PRIORITY_COLORS[order.priority] || PRIORITY_COLORS.normal) +
        (isDragging ? ' opacity-50 shadow-lg scale-105' : ' hover:shadow-md')}
      onClick={onClick}
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="font-mono text-xs">
            {order.order_number}
          </Badge>
          {order.is_paused && (
            <Badge className="bg-amber-500 text-white text-xs">
              <Pause className="w-3 h-3 mr-1" />Pausada
            </Badge>
          )}
        </div>

        <p className="font-medium text-sm line-clamp-2">{order.product_name}</p>

        {order.customer && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Building2 className="w-3 h-3" />
            <span className="truncate">{order.customer.company_name}</span>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Package className="w-3 h-3" />
            <span>{order.quantity} uds</span>
          </div>

          {order.operator ? (
            <div className="flex items-center gap-1 text-primary">
              <User className="w-3 h-3" />
              <span className="truncate max-w-[80px]">{order.operator.full_name}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-amber-500">
              <User className="w-3 h-3" />
              <span>Sin asignar</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  if (isDragging) {
    return cardContent;
  }

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        {cardContent}
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="right" align="start">
        <HoverContent order={order} />
      </HoverCardContent>
    </HoverCard>
  );
}
